import { LinearGradient } from 'expo-linear-gradient';
import moment from 'moment-timezone';
import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, ScrollView } from 'react-native';
import { useMediaQuery } from 'react-responsive';

import {
  useBillStatementsQuery,
  useConsumerUsageSummaryQuery,
} from 'apollo/queries/autogenerate/hooks';
import { billStatementFragment } from 'apollo/queries/autogenerate/operations';
import { BillStatement } from 'apollo/queries/autogenerate/schemas';
import { BillStatementStatus } from 'apollo/queries/autogenerate/schemas-public';
import { supplyMissingUsages } from 'screens/StatementScreen/StatementScreen/MonthlyUsageBars';
import { MONTHLY_USAGE_BAR_MIN_MONTHS } from 'shared/modules/usage/utils/utils';
import {
  getNumberRoundedToTens,
  getNumberRoundedToTensInJapanese,
} from 'shared/modules/usage/utils/utils';
import { formatToCurrency } from 'shared/utils/japanese';

const getMonthYearFromBillingCycle = (billingCycle: string) => {
  // billingCycle format: '2021-05'
  const [year, month] = billingCycle.split('-').map(Number);

  return { month, year };
};

const getColor = (billingCycle: string) => {
  const currentMonth = moment().tz('Asia/Tokyo');
  const usageMonth = moment(billingCycle).tz('Asia/Tokyo');

  const shouldHighLight =
    usageMonth.isBefore(currentMonth) || usageMonth === currentMonth;

  return shouldHighLight
    ? { text: '#2a2e32', bar: ['#e5258c', '#823cd7'] }
    : { text: '#a1aab0', bar: ['#F2D0E2', '#F2D0E2'] };
};

const getBillsByBillingCycle = (bills: BillStatement[] | undefined) => {
  const billsByBillingCycle: Record<string, billStatementFragment> = {};

  if (bills) {
    bills.forEach((bill) => {
      const billingCycle = moment(bill.period.end).format('YYYY-MM');

      billsByBillingCycle[billingCycle] = bill;
    });
  }

  return billsByBillingCycle;
};

const getUsageBarHeightProps = (isBigScreen: boolean) => {
  return {
    maxHeight: isBigScreen ? 120 : 90,
    minHeight: 2,
  };
};

type Props = {
  monthlyUsageBarScrollviewRef?: any;
};

export default function MonthlyUsageBars({
  monthlyUsageBarScrollviewRef,
}: Props) {
  const isBigScreen = useMediaQuery({ minWidth: 768 });
  const [barWidth, setBarWidth] = useState(0);
  const { data: usageSummaryData } = useConsumerUsageSummaryQuery();

  const largestUsageAmount: number = useMemo(() => {
    if (!usageSummaryData) {
      return 1;
    }

    let biggestUsage = 1;

    usageSummaryData.consumerUsageSummary.monthlyTotalUsagesV3.forEach(
      ({ usage }) => {
        if (usage > biggestUsage) {
          biggestUsage = usage;
        }
      }
    );

    return biggestUsage;
  }, [usageSummaryData]);

  if (!usageSummaryData) {
    return null;
  }

  const {
    upperRangeInJapanese,
    midRangeInJapanese,
  } = getNumberRoundedToTensInJapanese(largestUsageAmount);

  const upperRange = getNumberRoundedToTens(largestUsageAmount);

  const monthlyUsageData = supplyMissingUsages(
    usageSummaryData?.consumerUsageSummary?.monthlyTotalUsagesV3
  );

  const noUsageHistory = monthlyUsageData.every((elem) => elem.usage === 0);

  return (
    <View
      onLayout={(e) => {
        setBarWidth(
          e.nativeEvent.layout.width / MONTHLY_USAGE_BAR_MIN_MONTHS - 25 || 0
        );
      }}
    >
      {noUsageHistory && (
        <Text style={styles.noUsageHistoryLabel}>
          表示できるご利用履歴はありません。
        </Text>
      )}

      <ScrollView
        horizontal={true}
        style={styles.container}
        contentContainerStyle={{ width: 'inherit' }}
        ref={monthlyUsageBarScrollviewRef}
      >
        {barWidth &&
          monthlyUsageData.map(({ usage, billingCycle }, index) => {
            return (
              <MonthlyBar
                barWidth={barWidth}
                usage={usage}
                billingCycle={billingCycle}
                index={index}
                upperRange={upperRange}
                key={index}
              />
            );
          })}
        <View
          style={[
            styles.baseLine,
            {
              top: getUsageBarHeightProps(isBigScreen).maxHeight,
            },
          ]}
        />
      </ScrollView>
      {largestUsageAmount !== 1 && (
        <>
          <Text style={styles.range}>{upperRangeInJapanese}</Text>
          <Text
            style={[
              styles.range,
              { top: getUsageBarHeightProps(isBigScreen).maxHeight / 2 - 4 },
            ]}
          >
            {midRangeInJapanese}
          </Text>
          <Text
            style={[
              styles.range,
              {
                top: getUsageBarHeightProps(isBigScreen).maxHeight - 10,
              },
            ]}
          >
            {` 0`}
          </Text>
        </>
      )}
    </View>
  );
}

const MonthlyBar = ({
  barWidth,
  usage,
  billingCycle,
  upperRange,
  index,
}: {
  barWidth: number;
  usage: number;
  billingCycle: string;
  index: number;
  upperRange: number;
}) => {
  const isBigScreen = useMediaQuery({ minWidth: 768 });
  const { month } = getMonthYearFromBillingCycle(billingCycle);
  const { data: bills } = useBillStatementsQuery({
    fetchPolicy: 'cache-and-network',
  });
  const billsByBillingCycle = useMemo(
    () => getBillsByBillingCycle(bills?.billStatements as BillStatement[]),
    [bills]
  );
  const isOverdue =
    billsByBillingCycle[billingCycle]?.status === BillStatementStatus.OVERDUE;

  const color = getColor(billingCycle);
  const usageBarHeight = Math.floor(
    (usage / upperRange) * getUsageBarHeightProps(isBigScreen).maxHeight
  );

  return (
    <View
      style={[styles.monthlyUsageContainer, { width: barWidth }]}
      key={index}
    >
      {/* Putting the style here as the anchor will be inverted if move to styles  */}
      <View
        style={{
          height: getUsageBarHeightProps(isBigScreen).maxHeight,
          justifyContent: 'flex-end',
          marginBottom: 8,
        }}
      >
        <LinearGradient
          colors={color.bar}
          style={[
            styles.monthlyUsageAmount,
            {
              height:
                usageBarHeight < getUsageBarHeightProps(isBigScreen).minHeight
                  ? getUsageBarHeightProps(isBigScreen).minHeight
                  : usageBarHeight,
              borderRadius: 4,
            },
          ]}
        />
      </View>
      <Text style={[styles.monthlyUsageText, { color: color.text }]}>
        {month}月分
      </Text>
      <Text style={[styles.monthlyUsageText, { color: color.text }]}>
        {formatToCurrency(usage)}
      </Text>
      {isOverdue && (
        <Text style={[styles.overdueText, isBigScreen && { fontSize: 10 }]}>
          お支払い待ち
        </Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    maxHeight: 'auto',
    flexDirection: 'row',
    marginLeft: 20,
  },
  monthlyUsageContainer: {
    alignItems: 'center',
    flex: 1,
    marginRight: 20,
  },
  monthlyUsageAmount: {
    width: 12,
    borderTopRightRadius: 4,
    borderTopLeftRadius: 4,
  },
  monthlyUsageText: {
    fontSize: 10,
    fontWeight: 'bold',
    lineHeight: 16,
  },
  overdueText: {
    color: '#ff2751',
    fontSize: 8,
    fontWeight: 'bold',
    lineHeight: 16,
  },
  baseLine: {
    height: 1,
    width: '100%',
    position: 'absolute',
    bottom: 70,
    backgroundColor: '#cfd4d8',
  },
  range: {
    fontSize: 8,
    fontWeight: '600',
    lineHeight: 12,
    position: 'absolute',
    top: -4,
    left: 0,
    color: '#a1aab0',
  },
  noUsageHistoryLabel: {
    fontSize: 13,
    fontWeight: '300',
    lineHeight: 16,
    position: 'absolute',
    color: '#a1aab0',
    width: '100%',
    textAlign: 'center',
    top: '15%',
  },
});
